import { useEffect, useMemo, useState, useCallback } from "react";
import api, { extractError, API } from "@/lib/api";
import { SectionHeader } from "@/components/SectionHeader";
import { StatusPill } from "@/components/StatusPill";
import { ClientSearchInput } from "@/components/ClientSearchInput";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Plus, Search, Paperclip, FileDown, Loader2, UserPlus, Users, ArrowLeft, MoreHorizontal, PencilLine, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { formatCompactINR, formatDate, formatNumber } from "@/lib/format";
import { cn } from "@/lib/utils";

const EMPTY_NEW = { date: new Date().toISOString().slice(0, 10), client_code: "", client_name: "", deposit_amount: 0, remark: "", file: null };
const EMPTY_EXISTING = { date: new Date().toISOString().slice(0, 10), client: null, deposit_amount: 0, remark: "", file: null };

export default function SubmissionsPage() {
  const { user } = useAuth();
  const canSubmit = user.role === "sales_executive";
  const isAdmin = user.role === "admin";
  // A manager can approve/reject when the admin has explicitly granted the
  // `can_approve_submissions` permission. Backend enforces the actual
  // hierarchy scope on every action; this flag only drives UI visibility.
  const canApprove = isAdmin || (user.role === "manager" && !!user.can_approve_submissions);
  // Admin-only surface: edit / delete a submission.
  const canManage = isAdmin;

  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [ctype, setCtype] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("choose"); // choose | new | existing
  const [loading, setLoading] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [existingForm, setExistingForm] = useState(EMPTY_EXISTING);

  // Admin edit
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = useCallback(async () => {
    const params = {};
    if (status !== "all") params.status = status;
    if (ctype !== "all") params.client_type = ctype;
    if (q) params.q = q;
    if (start) params.start_date = start;
    if (end) params.end_date = end;
    try {
      const { data } = await api.get("/submissions", { params });
      setRows(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  }, [status, ctype, q, start, end]);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => {
    setStep("choose");
    setNewForm(EMPTY_NEW);
    setExistingForm(EMPTY_EXISTING);
  };

  const submitNew = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("date", newForm.date);
      fd.append("client_type", "new");
      fd.append("client_code", newForm.client_code);
      fd.append("client_name", newForm.client_name);
      fd.append("deposit_amount", String(newForm.deposit_amount));
      fd.append("remark", newForm.remark || "");
      if (newForm.file) fd.append("attachment", newForm.file);
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("New client submission created — pending approval");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  const submitExisting = async () => {
    if (!existingForm.client) {
      toast.error("Please select a client from the suggestion list");
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("date", existingForm.date);
      fd.append("client_type", "existing");
      fd.append("client_code", existingForm.client.client_code);
      fd.append("client_name", existingForm.client.client_name);
      fd.append("deposit_amount", String(existingForm.deposit_amount));
      fd.append("remark", existingForm.remark || "");
      if (existingForm.file) fd.append("attachment", existingForm.file);
      await api.post("/submissions", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Existing client submission created — pending approval");
      setOpen(false); resetForm(); load();
    } catch (e) { toast.error(extractError(e)); }
    finally { setLoading(false); }
  };

  const startEdit = (r) => {
    setEditRow(r);
    setEditForm({
      date: r.date, client_name: r.client_name,
      deposit_amount: r.deposit_amount, accounts_opened: r.accounts_opened,
      remark: r.remark || "",
    });
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/submissions/${editRow.id}`, editForm);
      toast.success("Submission updated");
      setEditRow(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const remove = async (r) => {
    if (!window.confirm(`Delete submission for ${r.client_name} (${formatCompactINR(r.deposit_amount)})? This cannot be undone.`)) return;
    try {
      await api.delete(`/submissions/${r.id}`);
      toast.success("Submission deleted");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div data-testid="submissions-page">
      <SectionHeader
        title="Daily Submissions"
        description="Log daily client work — approvals flow to Admin."
        actions={
          canSubmit && (
            <Button onClick={() => { setOpen(true); resetForm(); }} data-testid="new-submission-btn">
              <Plus className="mr-2 h-4 w-4" /> New Submission
            </Button>
          )
        }
      />

      <Card className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-5">
          <div className="relative sm:col-span-2">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input data-testid="sub-search" placeholder="Search client, code, exec…" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger data-testid="sub-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Select value={ctype} onValueChange={setCtype}>
            <SelectTrigger data-testid="sub-ctype-filter"><SelectValue placeholder="Client Type" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Clients</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="existing">Existing</SelectItem>
            </SelectContent>
          </Select>
          <div className="flex gap-2">
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="sub-start" />
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="sub-end" />
          </div>
        </div>

        <div className="overflow-x-auto rounded-md border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40">
                <TableHead className="label-caps">Date</TableHead>
                <TableHead className="label-caps">Executive</TableHead>
                <TableHead className="label-caps">Client</TableHead>
                <TableHead className="label-caps">Type</TableHead>
                <TableHead className="label-caps text-right">Deposit</TableHead>
                <TableHead className="label-caps text-right">Accounts</TableHead>
                <TableHead className="label-caps">Status</TableHead>
                <TableHead className="label-caps">File</TableHead>
                {canManage && <TableHead className="w-12" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`sub-row-${r.id}`} className="hover:bg-muted/40">
                  <TableCell className="text-sm tabular-nums">{formatDate(r.date)}</TableCell>
                  <TableCell className="text-sm">{r.user_name || "—"}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{r.client_name}</div>
                    <div className="text-xs text-muted-foreground tabular-nums">{r.client_code}</div>
                  </TableCell>
                  <TableCell><StatusPill status={r.client_type}>{r.client_type}</StatusPill></TableCell>
                  <TableCell className="text-right tabular-nums">{formatCompactINR(r.deposit_amount)}</TableCell>
                  <TableCell className="text-right tabular-nums">{formatNumber(r.accounts_opened)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <StatusPill status={r.status} />
                      {r.status === "rejected" && r.rejection_reason && (
                        <span className="text-[10px] text-muted-foreground italic">Reason: {r.rejection_reason}</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {r.attachment_path ? (
                      <a data-testid={`sub-file-${r.id}`} href={`${API}/submissions/attachment/${r.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-primary hover:underline">
                        <FileDown className="h-3.5 w-3.5" /> View
                      </a>
                    ) : (<span className="text-xs text-muted-foreground">—</span>)}
                  </TableCell>
                  {canManage && (
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`sub-menu-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => startEdit(r)}><PencilLine className="mr-2 h-4 w-4" /> Edit</DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-[hsl(var(--danger))] focus:text-[hsl(var(--danger))]" onClick={() => remove(r)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={canManage ? 9 : 8} className="py-10 text-center text-sm text-muted-foreground">No submissions found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Submission Dialog — two-step */}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) resetForm(); }}>
        <DialogContent className="max-w-2xl" data-testid="daily-submission-form">
          <DialogHeader>
            <DialogTitle>
              {step === "choose" && "New submission"}
              {step === "new" && (<span className="flex items-center gap-2"><button onClick={() => setStep("choose")} className="rounded-md p-1 hover:bg-muted" data-testid="sub-back-btn"><ArrowLeft className="h-4 w-4" /></button> New client submission</span>)}
              {step === "existing" && (<span className="flex items-center gap-2"><button onClick={() => setStep("choose")} className="rounded-md p-1 hover:bg-muted" data-testid="sub-back-btn"><ArrowLeft className="h-4 w-4" /></button> Existing client submission</span>)}
            </DialogTitle>
          </DialogHeader>

          {step === "choose" && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <TypeCard
                testid="choose-new-client"
                icon={<UserPlus className="h-6 w-6" />}
                title="New Client"
                description="Open a brand new client account. This also opens 1 account toward your target."
                onClick={() => setStep("new")}
              />
              <TypeCard
                testid="choose-existing-client"
                icon={<Users className="h-6 w-6" />}
                title="Existing Client"
                description="Log a deposit for a client already in your hierarchy. No new account is opened."
                onClick={() => setStep("existing")}
              />
            </div>
          )}

          {step === "new" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" data-testid="new-form-date" value={newForm.date} onChange={(e) => setNewForm({ ...newForm, date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Client Code</Label>
                <Input data-testid="new-form-code" value={newForm.client_code} onChange={(e) => setNewForm({ ...newForm, client_code: e.target.value.toUpperCase() })} placeholder="e.g. CL099" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Client Name</Label>
                <Input data-testid="new-form-name" value={newForm.client_name} onChange={(e) => setNewForm({ ...newForm, client_name: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Deposit Amount (₹)</Label>
                <Input type="number" min={0} data-testid="new-form-amount" value={newForm.deposit_amount} onChange={(e) => setNewForm({ ...newForm, deposit_amount: Number(e.target.value || 0) })} className="tabular-nums" />
              </div>
              <div className="space-y-1.5">
                <Label>Attachment (optional)</Label>
                <Input type="file" data-testid="new-form-file" onChange={(e) => setNewForm({ ...newForm, file: e.target.files?.[0] || null })} className="cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-xs" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Remark</Label>
                <Textarea data-testid="new-form-remark" value={newForm.remark} onChange={(e) => setNewForm({ ...newForm, remark: e.target.value })} rows={3} />
              </div>
              <div className="sm:col-span-2 rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                On approval → this counts as <b className="text-foreground">+1 Account Opening</b> against your weekly &amp; monthly account targets.
              </div>
            </div>
          )}

          {step === "existing" && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Search client</Label>
                <ClientSearchInput
                  value={existingForm.client}
                  onSelect={(c) => setExistingForm({ ...existingForm, client: c })}
                  testid="existing-form-client"
                />
                {existingForm.client && (
                  <div className="mt-1 text-xs text-muted-foreground">
                    Selected: <b className="text-foreground">{existingForm.client.client_name}</b> · <span className="font-mono">{existingForm.client.client_code}</span>
                  </div>
                )}
              </div>
              <div className="space-y-1.5">
                <Label>Date</Label>
                <Input type="date" data-testid="existing-form-date" value={existingForm.date} onChange={(e) => setExistingForm({ ...existingForm, date: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Deposit Amount (₹)</Label>
                <Input type="number" min={0} data-testid="existing-form-amount" value={existingForm.deposit_amount} onChange={(e) => setExistingForm({ ...existingForm, deposit_amount: Number(e.target.value || 0) })} className="tabular-nums" />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Remark</Label>
                <Textarea data-testid="existing-form-remark" value={existingForm.remark} onChange={(e) => setExistingForm({ ...existingForm, remark: e.target.value })} rows={3} />
              </div>
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Attachment (optional)</Label>
                <Input type="file" data-testid="existing-form-file" onChange={(e) => setExistingForm({ ...existingForm, file: e.target.files?.[0] || null })} className="cursor-pointer file:mr-3 file:rounded-md file:border-0 file:bg-muted file:px-3 file:py-1.5 file:text-xs" />
              </div>
              <div className="sm:col-span-2 rounded-md border border-dashed border-border bg-muted/40 p-3 text-xs text-muted-foreground">
                Existing client deposits only advance your <b className="text-foreground">deposit</b> target — no new account is opened.
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="secondary" onClick={() => { setOpen(false); resetForm(); }}>Cancel</Button>
            {step === "new" && (
              <Button onClick={submitNew} disabled={loading || !newForm.client_code || !newForm.client_name} data-testid="new-form-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit new client
              </Button>
            )}
            {step === "existing" && (
              <Button onClick={submitExisting} disabled={loading || !existingForm.client} data-testid="existing-form-submit">
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit deposit
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(v) => !v && setEditRow(null)}>
        <DialogContent data-testid="edit-sub-dialog">
          <DialogHeader><DialogTitle>Edit submission</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={editForm.date || ""} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Client Name</Label><Input value={editForm.client_name || ""} onChange={(e) => setEditForm({ ...editForm, client_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Deposit (₹)</Label><Input type="number" min={0} value={editForm.deposit_amount ?? 0} onChange={(e) => setEditForm({ ...editForm, deposit_amount: Number(e.target.value || 0) })} className="tabular-nums" /></div>
            <div className="space-y-1.5"><Label>Accounts Opened</Label><Input type="number" min={0} value={editForm.accounts_opened ?? 0} onChange={(e) => setEditForm({ ...editForm, accounts_opened: Number(e.target.value || 0) })} className="tabular-nums" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Remark</Label><Textarea value={editForm.remark || ""} onChange={(e) => setEditForm({ ...editForm, remark: e.target.value })} rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEditRow(null)}>Cancel</Button>
            <Button onClick={saveEdit} data-testid="edit-sub-save">Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TypeCard({ icon, title, description, onClick, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={cn(
        "group flex flex-col items-start gap-3 rounded-xl border border-border bg-card p-5 text-left transition-all",
        "hover:border-primary/40 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-primary/40",
      )}
    >
      <div className="rounded-md bg-primary/10 p-2 text-primary">{icon}</div>
      <div>
        <div className="font-display text-base font-semibold">{title}</div>
        <p className="mt-1 text-xs text-muted-foreground">{description}</p>
      </div>
    </button>
  );
}
